import { FC, useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { MonthCalendar } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import theme from 'theme'
import { subtractMonths } from '../../../pages/reports/reports.utils'
import { dateFormat } from '../../../../lib/utils'
import { MonthPickerActions, MonthPickerWrapper } from './monthRangePicker.styles'

export interface MonthRangeResult {
  dateFrom: string
  dateTo: string
}

export interface MonthlyRangePickerV2Props {
  value?: string
  onMonthSelect?: (range: MonthRangeResult) => void
  onCancel?: () => void
}

const DATE_RANGE_PATTERN = /^(\d{4})-(\d{2})-\d{2} to (\d{4})-(\d{2})-\d{2}$/

const parseInitialRange = (value?: string): { start: string | null; end: string | null } => {
  if (!value) return { start: null, end: null }
  const match = value.match(DATE_RANGE_PATTERN)
  if (match) {
    const [, startYear, startMonth, endYear, endMonth] = match
    return { start: `${startYear}-${startMonth}`, end: `${endYear}-${endMonth}` }
  }
  return { start: null, end: null }
}

const MonthRangePickerV2: FC<MonthlyRangePickerV2Props> = ({ value, onMonthSelect, onCancel }) => {
  const today = dayjs()
  const todayMonthPref = dayjs(today).get('month')
  const todayMonth = `${todayMonthPref + 1}`.padStart(2, '0')
  const todayDay = dayjs(today).get('date')
  const todayYear = dayjs(today).get('year')
  const lastYear = new Date().getFullYear() - 1

  const initialRange = parseInitialRange(value)
  const [firstCalendarValue, setFirstCalendarValue] = useState(dayjs(`${lastYear}-01-01`))
  const [secondCalendarValue, setSecondCalendarValue] = useState(dayjs(`${todayYear}-01-01`))
  const [startMonth, setStartMonth] = useState<string | null>(initialRange.start)
  const [endMonth, setEndMonth] = useState<string | null>(initialRange.end)
  const [validationError, setValidationError] = useState<string>('')

  const handleMonthClick = (newValue: any, year: number) => {
    const month = dayjs(newValue).get('month')
    const monthString = `${year}-${(month + 1).toString().padStart(2, '0')}`
    const clickedDate = dayjs(monthString)

    if (!startMonth || (startMonth && endMonth)) {
      // Start new selection
      setStartMonth(monthString)
      setEndMonth(null)
      setValidationError('')
    } else if (monthString === startMonth) {
      // Clicking same month - complete as single month selection
      setEndMonth(monthString)
      setValidationError('')
    } else {
      // Complete the range
      const startDate = dayjs(startMonth)

      if (clickedDate.isBefore(startDate)) {
        // Clicked before start, swap them
        const monthsDiff = startDate.diff(clickedDate, 'month')
        if (monthsDiff > 11) {
          setValidationError('Maximum range is 12 months')
          return
        }
        setEndMonth(startMonth)
        setStartMonth(monthString)
        setValidationError('')
      } else {
        // Clicked after start
        const monthsDiff = clickedDate.diff(startDate, 'month')

        if (monthsDiff > 11) {
          // More than 1 year
          setValidationError('Maximum range is 12 months')
        } else {
          setEndMonth(monthString)
          setValidationError('')
        }
      }
    }
  }

  const handleChangeFirst = (newValue: any) => {
    setFirstCalendarValue(newValue)
    handleMonthClick(newValue, lastYear)
  }

  const handleChangeSecond = (newValue: any) => {
    setSecondCalendarValue(newValue)
    handleMonthClick(newValue, todayYear)
  }

  const isMonthInRange = (monthString: string) => {
    if (!startMonth) return false
    if (!endMonth) return monthString === startMonth

    const current = dayjs(monthString)
    const start = dayjs(startMonth)
    const end = dayjs(endMonth)

    return (current.isSame(start) || current.isAfter(start)) && (current.isSame(end) || current.isBefore(end))
  }

  const handleOk = () => {
    if (startMonth) {
      const dateFrom = dayjs(startMonth).startOf('month').format('YYYY-MM-DD')
      const dateTo = endMonth
        ? dayjs(endMonth).endOf('month').format('YYYY-MM-DD')
        : dayjs(startMonth).endOf('month').format('YYYY-MM-DD')
      onMonthSelect?.({
        dateFrom,
        dateTo
      })
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction="column">
        <MonthPickerWrapper className="month-picker-monthly-wrapper" isopen="true">
          <Box className="month-calendar-container">
            <Typography className="month-picker-monthly-title">{lastYear}</Typography>
            <MonthCalendar
              value={firstCalendarValue}
              disableHighlightToday={true}
              shouldDisableMonth={(currentDay) => {
                const currentMonth = `${dayjs(currentDay).get('month') + 1}`.padStart(2, '0')
                const currentYear = dayjs(currentDay).get('year')
                const today = new Date()
                const pastYear = subtractMonths(today, 13)
                const currentDayFormatted = dayjs(currentDay).format(dateFormat.customLabel)

                return (
                  (todayYear === currentYear && todayMonth === currentMonth && todayDay < 15) ||
                  new Date(currentDayFormatted) > today ||
                  new Date(currentDayFormatted) < pastYear
                )
              }}
              onChange={handleChangeFirst}
              monthsPerRow={4}
              disableFuture
              sx={{
                '.MuiPickersMonth-root': {
                  '.MuiPickersMonth-monthButton': {
                    '&.Mui-selected': {
                      background: 'transparent !important',
                      color: `${theme.colors.dustyRoseGray} !important`
                    },
                    '&.Mui-disabled': {
                      opacity: 0.38
                    }
                  },
                  '&': (() => {
                    const styles: any = {}
                    for (let i = 0; i < 12; i++) {
                      const monthString = `${lastYear}-${(i + 1).toString().padStart(2, '0')}`
                      if (isMonthInRange(monthString)) {
                        styles[`&:nth-of-type(${i + 1}) .MuiPickersMonth-monthButton`] = {
                          backgroundColor: `${theme.baseColors.primary[20]} !important`,
                          color: `${theme.colors.white} !important`
                        }
                      }
                    }
                    return styles
                  })()
                }
              }}
            />
          </Box>
          <Box className="month-calendar-container">
            <Typography className="month-picker-monthly-title">{todayYear}</Typography>
            <MonthCalendar
              value={secondCalendarValue}
              onChange={handleChangeSecond}
              monthsPerRow={4}
              shouldDisableMonth={(currentDay) => {
                const currentMonth = `${dayjs(currentDay).get('month') + 1}`.padStart(2, '0')
                const currentYear = dayjs(currentDay).get('year')
                const today = new Date()
                const pastYear = subtractMonths(today, 13)
                const currentDayFormatted = dayjs(currentDay).format(dateFormat.customLabel)

                return (
                  (todayYear === currentYear && todayMonth === currentMonth && todayDay < 15) ||
                  new Date(currentDayFormatted) > today ||
                  new Date(currentDayFormatted) < pastYear
                )
              }}
              disableFuture
              sx={{
                '.MuiPickersMonth-root': {
                  '.MuiPickersMonth-monthButton': {
                    '&.Mui-selected': {
                      background: 'transparent !important',
                      color: `${theme.colors.dustyRoseGray} !important`
                    },
                    '&.Mui-disabled': {
                      opacity: 0.38
                    }
                  },
                  '&': (() => {
                    const styles: any = {}
                    for (let i = 0; i < 12; i++) {
                      const monthString = `${todayYear}-${(i + 1).toString().padStart(2, '0')}`
                      if (isMonthInRange(monthString)) {
                        styles[`&:nth-of-type(${i + 1}) .MuiPickersMonth-monthButton`] = {
                          backgroundColor: `${theme.baseColors.primary[20]} !important`,
                          color: `${theme.colors.white} !important`
                        }
                      }
                    }
                    return styles
                  })()
                }
              }}
            />
          </Box>
        </MonthPickerWrapper>

        <MonthPickerActions direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          {validationError ? (
            <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem', paddingLeft: '16px' }}>
              {validationError}
            </Typography>
          ) : (
            <Box />
          )}
          <Stack direction="row" spacing={1}>
            <Button variant="text" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="text" disabled={!startMonth} onClick={handleOk}>
              Ok
            </Button>
          </Stack>
        </MonthPickerActions>
      </Stack>
    </LocalizationProvider>
  )
}

export default MonthRangePickerV2
